import type {
  ComponentPropsWithoutRef,
  ElementType,
  KeyboardEvent,
  ReactNode,
} from 'react'

import type { AccessibleRole } from './accessible-div.helpers'
import { isActivationKey } from './accessible-div.helpers'
import * as styles from './accessible-div.styles'

type Props<T extends ElementType> = {
  as?: T
  role: AccessibleRole
  onActivate: () => void
  className?: string
  children?: ReactNode
} & Omit<
  ComponentPropsWithoutRef<T>,
  'role' | 'tabIndex' | 'onClick' | 'onKeyDown'
>

export const AccessibleDiv = <T extends ElementType = 'div'>({
  as,
  role,
  onActivate,
  className,
  children,
  ...rest
}: Props<T>) => {
  const Element = as || 'div'

  const handleClick = () => {
    onActivate()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // keys pressed on nested links or buttons keep their native behaviour
    if (event.target !== event.currentTarget) {
      return
    }

    if (!isActivationKey(role, event.key)) {
      return
    }

    event.preventDefault()
    onActivate()
  }

  return (
    <Element
      className={className}
      css={styles.focusable}
      role={role}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </Element>
  )
}
